import { createAsyncThunk } from "@reduxjs/toolkit";
import type { NavigateFunction } from "react-router-dom";
import { getClasses } from "../../../../services/api/instances/classificatorApi/methods";
import type {
  ClassifierValue,
  ClassPostObjType,
} from "../../../../services/api/instances/classificatorApi/types";
import {
  addChildToObject,
  createStorageObject,
  deleteChildObject,
  deleteStorageObject,
  getAllStorageObjects,
  updateStorageObject,
} from "../../../../services/api/instances/storageApi/methods";
import { getCompModels } from "../../../../services/api/methods";
import i18n from "../../../../services/i18n";
import { createStorageObjectApi } from "../../../../services/lib/storageObjectManager";
import { getServicePagePath } from "../../../../services/navigation/navigateToRoute";
import type { RootState } from "../../../../services/redux/types";
import {
  getCountOfFolders,
  getCountOfObjects,
} from "../../../../shared/utils/objectStorageFunctions";
import { CLASSIFICATION_INITIAL_VALUE } from "../../constants";
import {
  setClassifierResultsAction,
  setClassifiersAction,
  setSelectedClassifierAction,
} from "../slice";
import { getAllClassifierObjectVersions } from "./storage-versioning-actions";
import { StorageObject } from "../../../../services/api/instances/storageApi/types/object_types";
import { getError } from "../../../../services/lib/getError";

export const getCompModelsAction = createAsyncThunk(
  "classifier/getClassifierModels",
  async (_, { rejectWithValue }) => {
    try {
      return await getCompModels();
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getAllClassifiersAction = createAsyncThunk(
  "classifier/getAllClassifiers",
  async (_, { rejectWithValue }) => {
    try {
      return await getAllStorageObjects<ClassifierValue>("classify");
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createClassifierAction = createAsyncThunk(
  "classifier/createClassifier",
  async (
    {
      classifierType,
      name,
      withoutCount,
    }: {
      classifierType: "object" | "folder" | "copy";
      name?: string;
      withoutCount?: boolean;
    },
    ThunkAPI,
  ) => {
    try {
      const { classifiers, selectedClassifier } = (
        ThunkAPI.getState() as RootState
      ).classifierState;
      const defaultName =
        classifierType === "folder"
          ? name
            ? name
            : i18n.t("classification.default_folder_name")
          : i18n.t("classification.default_name");
      const count =
        classifierType === "object" || classifierType === "copy"
          ? getCountOfObjects(classifiers)
          : getCountOfFolders(classifiers);

      const classifier = {
        friendly_name: !withoutCount
          ? `${defaultName} ${count + 1}`
          : `${defaultName}`,
        value:
          classifierType === "folder"
            ? "null"
            : JSON.stringify({
                classifier_object:
                  (classifierType === "copy" &&
                    selectedClassifier?.value.classifier_object) ||
                  (classifierType === "object" && CLASSIFICATION_INITIAL_VALUE),
              }),
      };

      return await createStorageObject<ClassifierValue>(classifier, "classify");
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateStorageObjectAction = createAsyncThunk(
  "classifier/updateClassifier",
  async (postObj: StorageObject<ClassifierValue>, { rejectWithValue }) => {
    try {
      return await updateStorageObject<ClassifierValue>(postObj, "classify");
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateClassifierWithChildrenAction = createAsyncThunk(
  "dialogs/updateDialogWithChildren",
  async (
    body: { childId: string | null; parentId: string },
    { rejectWithValue },
  ) => {
    try {
      const { childId, parentId } = body;

      return await addChildToObject(childId as string, parentId, "classify");
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteChildrenAction = createAsyncThunk(
  "dialogs/updateDialogWithChildren",
  async (body: { childId: string; parentId: string }, { rejectWithValue }) => {
    try {
      const { childId, parentId } = body;
      return await deleteChildObject(childId, parentId);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteClassifierAction = createAsyncThunk(
  "classifier/deleteClassifier",
  async (body: { id: string; navigate: NavigateFunction }, thunkAPI) => {
    try {
      const { navigate, id } = body;
      const { classifiers, selectedClassifier } = (
        thunkAPI.getState() as RootState
      ).classifierState;
      const storage = createStorageObjectApi<ClassifierValue>(classifiers);
      const folder = storage.findParent(id);

      if (folder !== null) {
        const nextFolderClassifier = folder.children?.filter(
          (classifier: StorageObject<ClassifierValue>) => classifier.id !== id,
        ) as StorageObject<ClassifierValue>[];
        const updatedFolder = {
          ...folder,
          children: [
            ...(nextFolderClassifier as StorageObject<ClassifierValue>[]),
          ],
        };
        const lastFolderClassifier = nextFolderClassifier?.at(-1);
        const updatedClassifiers = classifiers.map((classifier) => {
          if (classifier.id === updatedFolder.id) {
            return updatedFolder;
          } else {
            return classifier;
          }
        });
        thunkAPI.dispatch(setClassifiersAction(updatedClassifiers));

        if (!nextFolderClassifier[0]) {
          navigate(getServicePagePath(folder.id!, "classify"));
          thunkAPI.dispatch(setSelectedClassifierAction(folder));
          thunkAPI.dispatch(setClassifierResultsAction(null));
        } else if (selectedClassifier?.id === id) {
          thunkAPI.dispatch(
            setSelectedClassifierAction(
              lastFolderClassifier as StorageObject<ClassifierValue>,
            ),
          );
          thunkAPI.dispatch(
            getAllClassifierObjectVersions(lastFolderClassifier?.id!),
          );
          navigate(getServicePagePath(lastFolderClassifier?.id!, "classify"));
        }
      }

      if (folder === null) {
        const nextClassifier = storage.removeById(id);
        const lastClassifier = nextClassifier[nextClassifier.length - 1];
        thunkAPI.dispatch(setClassifiersAction(nextClassifier));

        if (!nextClassifier[0]) {
          navigate(getServicePagePath(null, "classify"));
          thunkAPI.dispatch(setSelectedClassifierAction(null));
          thunkAPI.dispatch(setClassifiersAction([]));
          thunkAPI.dispatch(setClassifierResultsAction(null));
        } else if (selectedClassifier?.id === id) {
          thunkAPI.dispatch(setSelectedClassifierAction(lastClassifier));
          thunkAPI.dispatch(getAllClassifierObjectVersions(lastClassifier.id));
          thunkAPI.dispatch(setClassifierResultsAction(null));
          navigate(getServicePagePath(lastClassifier.id!, "classify"));
        }
      }

      return await deleteStorageObject(id, "classify");
    } catch (e: unknown) {
      const message = getError(e);
      return thunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getClassesAction = createAsyncThunk(
  "classifier/getClasses",
  async (postObj: ClassPostObjType, { rejectWithValue }) => {
    try {
      return await getClasses(postObj);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
